<template>
  <el-config-provider :locale="locale">
    <router-view />
    <!-- AI 智能助手悬浮按钮 — 仅在 project 页面显示；可拖动，位置记忆 -->
    <div
      v-if="isProjectPage"
      class="ai-float-btn"
      :class="{ dragging: isDragging }"
      :style="btnStyle"
      @mousedown="onMouseDown"
      @click="onBtnClick"
    >
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
        <path d="M7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z"/>
      </svg>
      <span class="ai-btn-text">AI 助手</span>
    </div>
  </el-config-provider>
</template>

<script setup>
import { ref, computed } from 'vue';
import { ElConfigProvider } from 'element-plus';
import zhCn from 'element-plus/lib/locale/lang/zh-cn';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();

// 当前平台自己的 AI 分析页路径（按当前路由前缀推导；不再写死 MECS）
const analysisTargetPath = () => {
  const p = route.path;
  if (p.startsWith('/des_mw')) return '/des_mw/project/analysis';
  if (p.startsWith('/des_tes')) return '/des_tes/project/analysis';
  if (p.startsWith('/mecs')) return '/mecs/project/analysis';
  return null;
};

// 仅在 project 相关页面、且当前平台确实注册了（菜单权限允许的）AI 分析页时显示按钮；
// 已在 analysis（AI 分析）页面时隐藏——点击本按钮跳转的正是该页，已在此页再显示无意义
const isProjectPage = computed(() => {
  if (!route.path.includes('/project') || route.path.includes('/analysis')) return false;
  const target = analysisTargetPath();
  return !!target && router.getRoutes().some((r) => r.path === target);
});

const goToAI = () => {
  const target = analysisTargetPath();
  if (target) router.push(target);
};

// ===== AI 助手按钮拖拽（默认位置 right:24px/bottom:80px，拖后记忆到 localStorage）=====
const AI_BTN_POS_KEY = 'ai_float_btn_pos';
const btnPos = ref(null); // {left, top} 或 null=默认定位
const isDragging = ref(false);
let dragState = null; // {startX, startY, baseLeft, baseTop, moved}
let justDragged = false;

// 启动时恢复记忆位置
try {
  const saved = localStorage.getItem(AI_BTN_POS_KEY);
  if (saved) btnPos.value = JSON.parse(saved);
} catch (e) {
  /* 忽略损坏的记忆 */
}

const btnStyle = computed(() => {
  if (!btnPos.value) return {};
  // 拖过之后用 left/top 定位（覆盖默认 right/bottom）
  return {
    left: btnPos.value.left + 'px',
    top: btnPos.value.top + 'px',
    right: 'auto',
    bottom: 'auto',
  };
});

const onMouseDown = (e) => {
  if (e.button !== 0) return; // 仅左键
  const rect = e.currentTarget.getBoundingClientRect();
  dragState = {
    startX: e.clientX,
    startY: e.clientY,
    baseLeft: rect.left,
    baseTop: rect.top,
    moved: false,
  };
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  e.preventDefault();
};

const onMouseMove = (e) => {
  if (!dragState) return;
  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;
  // 超过 3px 视为拖动（区分点击）
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragState.moved = true;
  if (!dragState.moved) return;
  isDragging.value = true;
  // 边界限制（按钮 60x60）
  const newLeft = Math.min(Math.max(dragState.baseLeft + dx, 0), window.innerWidth - 60);
  const newTop = Math.min(Math.max(dragState.baseTop + dy, 0), window.innerHeight - 60);
  btnPos.value = { left: newLeft, top: newTop };
};

const onMouseUp = () => {
  if (!dragState) return;
  justDragged = dragState.moved;
  isDragging.value = false;
  // 拖过则记忆位置
  if (dragState.moved && btnPos.value) {
    localStorage.setItem(AI_BTN_POS_KEY, JSON.stringify(btnPos.value));
  }
  dragState = null;
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup', onMouseUp);
};

// 点击：拖动刚结束时本次点击不跳转（避免误触）
const onBtnClick = () => {
  if (justDragged) {
    justDragged = false;
    return;
  }
  goToAI();
};

// ElementPlus全局中文显示 localStorage.getItem(VUE_ADMIN_LANG_LOCALE) =="zh-CN"
const localeObj = {
  ...zhCn,
  el: {
    ...zhCn.el,
    colorpicker: { confirm: '确认', clear: '清空' },
    messagebox: { title: '提示', confirm: '确认', cancel: '取消', error: '输入的数据不合法!' },
  }
};

const locale = ref(localeObj);

// 解决el-table 调整窗口报错
const debounce = (fn, delay) => {
  let timer = null;
  return function () {
    let context = this;
    let args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, delay);
  };
};
const _ResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
  constructor(callback) {
    callback = debounce(callback, 16);
    super(callback);
  }
};
</script>

<style lang="scss">
@import url(../public/css/iconfont.css);
@import url(../public/css/index.css);
html,
body {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}
#app {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  height: 100%;
  padding: 0;
  margin: 0;
  // VR样式
  body div canvas {
    width: 100% !important;
    height: 100% !important;
  }
}

// 自定义进度条颜色
#nprogress .bar {
  background: var(--color-primary) !important;
}

// AI 智能助手悬浮按钮
.ai-float-btn {
  position: fixed !important;
  right: 24px !important;
  bottom: 80px !important;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #409EFF, #337ecc);
  color: #fff;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transition: box-shadow 0.3s ease;
  z-index: 10000 !important;
  opacity: 0.15;
  &.dragging {
    cursor: grabbing;
    opacity: 0.9;
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(64, 158, 255, 0.6);
    transition: none;
  }
  &:hover {
    opacity: 1;
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(64, 158, 255, 0.6);
  }
  .ai-btn-text {
    font-size: 10px;
    font-weight: 500;
    white-space: nowrap;
    color: #fff;
  }
}
</style>
